import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  TagIcon,
  CubeIcon,
  ChartBarIcon,
  CheckCircleIcon,
  XCircleIcon,
  SparklesIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';

const AdminProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/products/${productId}`);
      setProduct(response.data.product || response.data);
    } catch (error) {
      toast.error('שגיאה בטעינת המוצר');
      navigate('/admin/products');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק מוצר זה?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('המוצר נמחק בהצלחה');
      navigate('/admin/products');
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה במחיקת המוצר');
    }
  };

  const handleToggleFeatured = async () => {
    try {
      await axios.post(`/api/featured-products/${productId}/feature`, {
        featured: !product.is_featured
      });
      setProduct(prev => ({ ...prev, is_featured: !prev.is_featured }));
      toast.success(product.is_featured ? 'המוצר הוסר מהטרנדים' : 'המוצר סומן כטרנד');
    } catch (error) {
      toast.error('שגיאה בעדכון סטטוס הטרנד');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">מוצר לא נמצא</h2>
          <Link to="/admin/products" className="text-blue-600 hover:underline">
            חזור לרשימת המוצרים
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/admin/products')}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          <span className="font-medium">חזרה למוצרים</span>
        </button>

        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="h-20 w-20 rounded-xl bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center shadow-lg">
              {product.images && product.images.length > 0 ? (
                <img
                  src={
                    product.images[0].startsWith('http')
                      ? product.images[0]
                      : `/api${product.images[0]}`
                  }
                  alt={product.name}
                  className="h-20 w-20 rounded-xl object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<svg class="h-10 w-10 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>';
                  }}
                />
              ) : (
                <PhotoIcon className="h-10 w-10 text-blue-600" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-gray-900">{product.name}</h1>
                {product.is_featured && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-orange-500 to-red-500 text-white text-sm font-bold rounded-full shadow-md">
                    🔥 טרנד
                  </span>
                )}
                {product.is_active ? (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 text-sm font-semibold rounded-full">
                    <CheckCircleIcon className="h-4 w-4" />
                    פעיל
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-800 text-sm font-semibold rounded-full">
                    <XCircleIcon className="h-4 w-4" />
                    לא פעיל
                  </span>
                )}
              </div>
              <p className="text-gray-600">{product.sku}</p>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleToggleFeatured}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold transition-all shadow-sm hover:shadow-md ${product.is_featured
                  ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white'
                  : 'bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
            >
              <SparklesIcon className="h-5 w-5" />
              {product.is_featured ? 'הסר מטרנדים' : 'סמן כטרנד'}
            </button>
            <button
              onClick={() => navigate(`/admin/products/edit/${productId}`)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-all shadow-sm hover:shadow-md"
            >
              <PencilIcon className="h-5 w-5" />
              ערוך
            </button>
            <button
              onClick={handleDelete}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-all shadow-sm hover:shadow-md"
            >
              <TrashIcon className="h-5 w-5" />
              מחק
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <TagIcon className="h-8 w-8 opacity-80" />
            <span className="text-sm font-medium opacity-90">מחיר B2C</span>
          </div>
          <p className="text-3xl font-bold">₪{parseFloat(product.price_b2c || 0).toFixed(2)}</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <TagIcon className="h-8 w-8 opacity-80" />
            <span className="text-sm font-medium opacity-90">מחיר B2B</span>
          </div>
          <p className="text-3xl font-bold">₪{parseFloat(product.price_b2b || 0).toFixed(2)}</p>
          {product.price_b2b && product.price_b2c && (
            <p className="text-sm mt-2 opacity-90">
              חיסכון {Math.round(((product.price_b2c - product.price_b2b) / product.price_b2c) * 100)}%
            </p>
          )}
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <CubeIcon className="h-8 w-8 opacity-80" />
            <span className="text-sm font-medium opacity-90">מלאי</span>
          </div>
          <p className="text-3xl font-bold">{(product.stock_b2c || 0) + (product.stock_b2b || 0)}</p>
          {((product.stock_b2c || 0) + (product.stock_b2b || 0)) <= (product.min_stock_level || product.low_stock_threshold || 10) && (
            <p className="text-sm mt-2 opacity-90">⚠️ מלאי נמוך!</p>
          )}
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <ChartBarIcon className="h-8 w-8 opacity-80" />
            <span className="text-sm font-medium opacity-90">נמכר</span>
          </div>
          <p className="text-3xl font-bold">{product.sold_count || 0}</p>
          <p className="text-sm mt-2 opacity-90">יחידות בסך הכל</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        {/* Tabs */}
        <div className="border-b border-gray-200 bg-gray-50">
          <div className="flex gap-4 px-6">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-4 font-semibold transition-all border-b-2 ${activeTab === 'overview'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
            >
              📋 סקירה כללית
            </button>
            <button
              onClick={() => setActiveTab('images')}
              className={`py-4 px-4 font-semibold transition-all border-b-2 ${activeTab === 'images'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
            >
              🖼️ תמונות
            </button>
            <button
              onClick={() => setActiveTab('inventory')}
              className={`py-4 px-4 font-semibold transition-all border-b-2 ${activeTab === 'inventory'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
            >
              📦 מלאי
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">תיאור המוצר</h3>
                <p className="text-gray-700 leading-relaxed">
                  {product.description || 'אין תיאור זמין'}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">📁 קטגוריה</h4>
                  <p className="text-gray-700">{product.category?.name || 'ללא קטגוריה'}</p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">🏷️ SKU</h4>
                  <p className="text-gray-700 font-mono">{product.sku}</p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">📅 תאריך יצירה</h4>
                  <p className="text-gray-700">
                    {new Date(product.createdAt).toLocaleDateString('he-IL')}
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">🔄 עודכן לאחרונה</h4>
                  <p className="text-gray-700">
                    {new Date(product.updatedAt).toLocaleDateString('he-IL')}
                  </p>
                </div>
              </div>

              {/* Perfume Specific Fields */}
              {(product.fragrance_family || product.occasion || product.intensity) && (
                <div className="mt-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <h4 className="font-semibold text-purple-900 mb-3">🌸 מאפייני בושם</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {product.fragrance_family && (
                      <div>
                        <p className="text-xs text-purple-600 mb-1">משפחת ניחוח</p>
                        <p className="font-medium text-purple-900">{product.fragrance_family}</p>
                      </div>
                    )}
                    {product.occasion && (
                      <div>
                        <p className="text-xs text-purple-600 mb-1">אירוע</p>
                        <p className="font-medium text-purple-900">{product.occasion}</p>
                      </div>
                    )}
                    {product.intensity && (
                      <div>
                        <p className="text-xs text-purple-600 mb-1">עוצמה</p>
                        <p className="font-medium text-purple-900">{product.intensity}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'images' && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">תמונות המוצר</h3>
              {product.images && product.images.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {product.images.map((image, index) => (
                    <div key={index} className="aspect-square rounded-xl overflow-hidden shadow-md border-2 border-gray-200 hover:border-blue-500 transition-colors">
                      <img
                        src={image.startsWith('http') ? image : `/api${image}`}
                        alt={`${product.name} ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <PhotoIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>אין תמונות זמינות</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'inventory' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-900 mb-2">📦 מלאי כללי</h4>
                  <p className="text-3xl font-bold text-blue-700">{(product.stock_b2c || 0) + (product.stock_b2b || 0)}</p>
                </div>

                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <h4 className="font-semibold text-green-900 mb-2">🏪 מלאי B2C</h4>
                  <p className="text-3xl font-bold text-green-700">{product.stock_b2c || 0}</p>
                </div>

                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <h4 className="font-semibold text-purple-900 mb-2">🏢 מלאי B2B</h4>
                  <p className="text-3xl font-bold text-purple-700">{product.stock_b2b || 0}</p>
                </div>
              </div>

              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <h4 className="font-semibold text-orange-900 mb-2">⚠️ סף מלאי נמוך</h4>
                <p className="text-2xl font-bold text-orange-700">{product.min_stock_level || product.low_stock_threshold || 10}</p>
                <p className="text-sm text-orange-600 mt-1">
                  {((product.stock_b2c || 0) + (product.stock_b2b || 0)) <= (product.min_stock_level || product.low_stock_threshold || 10)
                    ? 'המלאי נמוך! הזמן מלאי נוסף'
                    : 'המלאי תקין'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminProductDetail;
